import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, Info, CheckCircle, ChevronLeft, ChevronRight, Loader2, AlertCircle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { stocksAPI, getErrorMessage } from '../../lib/api';
import { toast } from '../../hooks/use-toast';
import { UpgradeOverlay } from '../../components/UpgradeOverlay';
import { useAccess } from '../../hooks/useAccess';

const StockRecommendationsContent = () => {
  const { liveIca } = useAccess();
  const maxSelections = liveIca.maxCustomStocks || 0;
  const itemsPerPage = 20;
  
  // State
  const [selectedStocks, setSelectedStocks] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingSearch, setPendingSearch] = useState('');
  const [selectionTrayExpanded, setSelectionTrayExpanded] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalStocks, setTotalStocks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectionsLoading, setSelectionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // stock id being acted upon

  // Fetch selected stocks on mount
  const fetchSelectedStocks = useCallback(async () => {
    setSelectionsLoading(true);
    try {
      const response = await stocksAPI.getSelectedStocks();
      const data = response.data?.data || response.data;
      setSelectedStocks(data?.selected_stocks || []);
    } catch (err) {
      console.error('Failed to fetch selected stocks:', err);
    } finally {
      setSelectionsLoading(false);
    }
  }, []);

  // Fetch stocks list
  const fetchStocks = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await stocksAPI.searchStocks({
        q: searchQuery,
        page: currentPage,
        limit: itemsPerPage,
      });
      
      const responseData = response.data;
      const stocksData = responseData?.data || [];
      const pagination = responseData?.pagination || {};
      
      setStocks(stocksData);
      setTotalStocks(pagination?.total || stocksData.length);
    } catch (err) {
      console.error('Failed to fetch stocks:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [searchQuery, currentPage]);

  // Initial load
  useEffect(() => {
    fetchSelectedStocks();
  }, [fetchSelectedStocks]);

  // Fetch stocks when search or page changes
  useEffect(() => {
    fetchStocks();
  }, [fetchStocks]);

  // Handle search
  const handleSearch = () => {
    setSearchQuery(pendingSearch);
    setCurrentPage(1);
  };

  // Handle search on Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Select a stock
  const handleSelectStock = async (stock) => {
    if (selectedStocks.length >= maxSelections) {
      toast.warning({
        title: 'Selection Limit Reached',
        description: `You can only select up to ${maxSelections} stocks.`,
      });
      return;
    }
    if (selectedStocks.find(s => s.id === stock.id)) return;
    
    setActionLoading(stock.id);
    try {
      await stocksAPI.updateSelection({
        stock_ids: [stock.id],
        action: 'select',
      });
      
      // Add to local state
      setSelectedStocks(prev => [...prev, {
        id: stock.id,
        name: stock.name,
        symbol: stock.symbol,
      }]);
      
      // Update the stocks list to reflect selection
      setStocks(prev => prev.map(s => 
        s.id === stock.id ? { ...s, selected: true } : s
      ));
      
      toast.success({
        title: 'Stock Selected',
        description: `${stock.name} (${stock.symbol}) has been added to your watchlist.`,
      });
    } catch (err) {
      console.error('Failed to select stock:', err);
      toast.error({
        title: 'Selection Failed',
        description: getErrorMessage(err) || 'Failed to select stock. Please try again.',
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Deselect a stock
  const handleDeselectStock = async (stockId) => {
    const stock = selectedStocks.find(s => s.id === stockId);
    setActionLoading(stockId);
    try {
      await stocksAPI.updateSelection({
        stock_ids: [stockId],
        action: 'deselect',
      });
      
      // Remove from local state
      setSelectedStocks(prev => prev.filter(s => s.id !== stockId));
      
      // Update the stocks list to reflect deselection
      setStocks(prev => prev.map(s => 
        s.id === stockId ? { ...s, selected: false } : s
      ));
      
      toast.info({
        title: 'Stock Removed',
        description: stock ? `${stock.name} has been removed from your watchlist.` : 'Stock has been removed from your watchlist.',
      });
    } catch (err) {
      console.error('Failed to deselect stock:', err);
      toast.error({
        title: 'Removal Failed',
        description: getErrorMessage(err) || 'Failed to remove stock. Please try again.',
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Toggle stock selection
  const handleToggleStock = (stock) => {
    const isSelected = selectedStocks.find(s => s.id === stock.id);
    if (isSelected) {
      handleDeselectStock(stock.id);
    } else {
      handleSelectStock(stock);
    }
  };

  const totalPages = Math.ceil(totalStocks / itemsPerPage);
  const remainingSelections = maxSelections - selectedStocks.length;

  // Loading state
  if (loading && selectionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-3 text-blue-600" size={48} />
          <p className="text-gray-600">Loading stocks...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && stocks.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-3 text-red-500" size={48} />
          <p className="text-red-600 font-semibold mb-2">Failed to load stocks</p>
          <p className="text-gray-500 text-sm mb-4">{error}</p>
          <button
            onClick={fetchStocks}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
          >
            <RefreshCw size={18} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Selected Stocks Display */}
      {selectedStocks.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <CheckCircle className="text-green-600" size={24} />
              My Watchlist ({selectedStocks.length})
            </h3>
            <button
              onClick={() => setSelectionTrayExpanded(!selectionTrayExpanded)}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium text-sm"
            >
              {selectionTrayExpanded ? (
                <>
                  <ChevronUp size={18} />
                  Collapse
                </>
              ) : (
                <>
                  <ChevronDown size={18} />
                  Expand
                </>
              )}
            </button>
          </div>
          {selectionTrayExpanded && (
            <div className="flex flex-wrap gap-2">
              {selectedStocks.map(stock => (
                <div 
                  key={stock.id} 
                  className="flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-semibold"
                >
                  <span>{stock.name}</span>
                  <button
                    onClick={() => handleDeselectStock(stock.id)}
                    disabled={actionLoading === stock.id}
                    className="hover:bg-blue-200 rounded-full p-1 transition-colors disabled:opacity-50"
                  >
                    {actionLoading === stock.id ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <X size={16} />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Progress Bar */}
      <div className="bg-blue-600 text-white rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white text-blue-600 rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg">
              {selectedStocks.length}/{maxSelections}
            </div>
            <span className="font-semibold">Stocks Selected</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white text-blue-600 rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg">
              {remainingSelections}
            </div>
            <span className="font-semibold">Remaining Selections</span>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Search className="text-blue-600" size={28} />
          Stock Search
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
          <div className="md:col-span-8">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Search Query:</label>
            <input
              type="text"
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter stock name or symbol..."
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>

          <div className="md:col-span-2 flex items-end">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Search size={20} />
              )}
              Search
            </button>
          </div>

          <div className="md:col-span-2 flex items-end">
            <button
              onClick={() => {
                setPendingSearch('');
                setSearchQuery('');
                setCurrentPage(1);
              }}
              disabled={loading || (!pendingSearch && !searchQuery)}
              className="w-full border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={20} />
              Clear
            </button>
          </div>
        </div>

        {/* How to Search Info */}
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
          <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
            <Info size={18} />
            How to Search
          </h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Use the search field to find stocks by Stock Name or Symbol</li>
            <li>• Search is case-insensitive and supports partial matches</li>
            <li>• Click "Add to Watchlist" to track a stock for LIVE announcements</li>
            <li>• You can add up to {maxSelections} stocks to your watchlist</li>
          </ul>
        </div>
      </div>

      {/* Available Stocks Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="bg-green-50 border-l-4 border-green-500 p-4 m-6 rounded-lg">
          <p className="text-sm text-green-800 flex items-center gap-2">
            <Info size={16} />
            Showing {stocks.length} of {totalStocks} stocks. Page {currentPage} of {totalPages || 1}.
          </p>
        </div>

        <div className="px-6 pb-2">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            ☰ All Available Stocks
            <span className="text-sm bg-gray-200 text-gray-700 px-3 py-1 rounded-full">{totalStocks} Total</span>
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-blue-600" size={40} />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">S.NO.</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">STOCK NAME</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">SCRIP CODE</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">SYMBOL</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">ACTION</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {stocks.map((stock, idx) => {
                    const isSelected = selectedStocks.find(s => s.id === stock.id) || stock.selected;
                    const isDisabled = !isSelected && selectedStocks.length >= maxSelections;
                    const isLoading = actionLoading === stock.id;
                    
                    return (
                      <tr 
                        key={stock.id} 
                        className={`hover:bg-gray-50 ${isDisabled ? 'opacity-50' : ''}`}
                      >
                        <td className="px-6 py-4 text-gray-900">
                          {(currentPage - 1) * itemsPerPage + idx + 1}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-gray-900 font-medium">{stock.name}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-mono text-sm text-gray-600">{stock.scrip_code || '-'}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-mono text-sm text-blue-600 font-semibold">{stock.symbol}</span>
                        </td>
                        <td className="px-6 py-4">
                          {isLoading ? (
                            <Loader2 size={20} className="animate-spin text-blue-600" />
                          ) : isSelected ? (
                            <button
                              onClick={() => handleDeselectStock(stock.id)}
                              className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap"
                            >
                              Remove from Watchlist
                            </button>
                          ) : (
                            <button
                              onClick={() => handleSelectStock(stock)}
                              disabled={isDisabled}
                              className="px-3 py-1.5 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                              Add to Watchlist
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {stocks.length === 0 && (
              <div className="text-center py-12">
                <Search className="mx-auto text-gray-300 mb-4" size={64} />
                <p className="text-gray-500 text-lg">No stocks found</p>
                <p className="text-gray-400 text-sm">Try adjusting your search query</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t p-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalStocks)} of {totalStocks} stocks
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || loading}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          disabled={loading}
                          className={`px-4 py-2 rounded-lg ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'border hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages || loading}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Selection Info */}
      {selectedStocks.length >= maxSelections && (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4">
          <p className="text-sm text-yellow-800 font-semibold flex items-center gap-2">
            <Info size={16} />
            You have reached the maximum selection limit ({maxSelections} stocks). Remove a stock to select a different one.
          </p>
        </div>
      )}
    </div>
  );
};

// Wrapper component with access control
const StockRecommendations = () => {
  return (
    <UpgradeOverlay service="stock_selection">
      <StockRecommendationsContent />
    </UpgradeOverlay>
  );
};

export default StockRecommendations;
